import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, TouchableOpacity } from 'react-native';
import { useLocalSearchParams, useRouter, Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../../src/api/client';
import { useToast } from '../../../src/components/Toast';
import Input from '../../../src/components/Input';
import Button from '../../../src/components/Button';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';

/** Default relay count when seeding placeholder tiers; matches the edit-bay fallback. */
const DEFAULT_RELAY_COUNT = 4;

type EquipType = 'automatic' | 'self-serve' | 'vacuum' | 'vending';

interface PackageRow {
  name: string;
  price: string;    // dollars, converted to cents on save
  minutes: string;  // whole minutes, converted to durationMs on save
}

const EQUIP_TYPES: { value: EquipType; label: string; icon: keyof typeof Ionicons.glyphMap; desc: string }[] = [
  { value: 'automatic', label: 'Automatic Bay', icon: 'car', desc: 'Fixed price wash tiers' },
  { value: 'self-serve', label: 'Self-Serve Bay', icon: 'timer', desc: 'Metered or timed packages' },
  { value: 'vacuum', label: 'Vacuum', icon: 'sparkles', desc: 'Metered or timed packages' },
  { value: 'vending', label: 'Vending Machine', icon: 'fast-food', desc: 'App-paid coin credit' },
];

/** Auto-fill minutes from a package price at the master rate ($/min). */
function minutesFromPrice(priceDollars: string, rateDollars: string): string {
  const price = parseFloat(priceDollars);
  const rate = parseFloat(rateDollars);
  if (!price || !rate || price <= 0 || rate <= 0) return '';
  return String(Math.max(1, Math.round(price / rate)));
}

/** AddEquipmentScreen - unified creation flow for all four equipment types.
 *  Self-serve bays and vacuums share the dual-family pricing model: one
 *  master Rate ($/min), then Metered (min/max charge → count-up sessions)
 *  and/or Timed Packages (name/price/minutes rows + custom amounts) — both
 *  families can be enabled at once; the segmented buttons only switch which
 *  section is visible. Boards, relays and pulse timing are assigned afterwards
 *  under Edit equipment → Controller Setup (round 2: no Advanced box, no
 *  controller picker at creation). */
export default function AddEquipmentScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const { locationId } = useLocalSearchParams<{ locationId: string }>();
  const [name, setName] = useState('');
  const [equipType, setEquipType] = useState<EquipType>('automatic');
  // ── Pricing family (self-serve + vacuum): the segmented buttons ARE the
  // enable choice — exactly one family is active at a time. ──
  const [rate, setRate] = useState('');                       // master $/min
  const [activeSection, setActiveSection] = useState<'metered' | 'packages'>('metered');
  const [minimumCharge, setMinimumCharge] = useState('3.00');
  const [maximumCharge, setMaximumCharge] = useState('');
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [allowCustomAmount, setAllowCustomAmount] = useState(false);
  /** Unlocks Minutes from the rate math so packages can carry bulk discounts
   *  (e.g. $3 buys 20 min at a 20¢/min rate). */
  const [overrideMinutes, setOverrideMinutes] = useState(false);
  // Vending fields
  const [maxCredit, setMaxCredit] = useState('5.00');
  const [vendPresets, setVendPresets] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  // Vending: credit per pulse (cents). Relay, pulse timing and the board are set under Edit equipment → Controller Setup (round 2).
  const [creditStepCents, setCreditStepCents] = useState('25');
  const router = useRouter();
  const toast = useToast();

  const dualFamily = equipType === 'self-serve' || equipType === 'vacuum';

  // Bidirectional auto-calc from the master rate: the last-edited field
  // drives — Price → Minutes = price ÷ rate; Minutes → Price = minutes × rate.
  // With the bulk-discount override on, minutes are unlocked from the rate:
  // fields edit independently (price still fills an EMPTY minutes as a hint).
  const updatePackage = (i: number, field: keyof PackageRow, v: string) => {
    setPackages(prev => {
      const u = [...prev];
      u[i] = { ...u[i], [field]: v };
      const rateNum = parseFloat(rate);
      if (field === 'price' && (!overrideMinutes || !u[i].minutes)) {
        const auto = minutesFromPrice(v, rate);
        if (auto) u[i] = { ...u[i], minutes: auto };
      } else if (field === 'minutes' && !overrideMinutes) {
        const mins = parseFloat(v);
        if (mins > 0 && rateNum > 0) u[i] = { ...u[i], price: (mins * rateNum).toFixed(2) };
      }
      return u;
    });
  };

  /** Validate + build the tiers payload from the package rows. Returns null
   *  after toasting on error; [] when no usable rows. */
  const buildPackages = (): { name: string; price: number; durationMs: number; enabled: boolean }[] | null => {
    const rows = packages.filter(r => r.name.trim() || r.price || r.minutes);
    const parsed = rows.map(r => ({
      name: r.name.trim(),
      price: Math.round(parseFloat(r.price) * 100),
      durationMs: Math.round(parseFloat(r.minutes) * 60000),
      enabled: true,
    }));
    if (parsed.some(t => !t.name)) { toast.show({ type: 'error', title: 'Each package needs a name' }); return null; }
    if (parsed.some(t => isNaN(t.price) || t.price <= 0)) { toast.show({ type: 'error', title: 'Each package needs a valid price' }); return null; }
    if (parsed.some(t => isNaN(t.durationMs) || t.durationMs < 60000 || t.durationMs > 3600000)) {
      toast.show({ type: 'error', title: 'Package minutes must be between 1 and 60' });
      return null;
    }
    return parsed;
  };

  const handleCreateVending = async () => {
    const step = parseInt(creditStepCents) || 25;
    if (step < 5 || step > 500) { toast.show({ type: 'error', title: 'Credit step must be 5–500 cents' }); return; }
    const maxCents = Math.round(parseFloat(maxCredit) * 100);
    if (!maxCents || maxCents < 25) { toast.show({ type: 'error', title: 'Enter a valid maximum credit (at least $0.25)' }); return; }
    const validPresets = vendPresets
      .filter(p => p && parseFloat(p) > 0)
      .map(p => Math.round(parseFloat(p) * 100));
    const badPreset = validPresets.find(p => p > maxCents || p % step !== 0);
    if (badPreset != null) {
      toast.show({ type: 'error', title: `Presets must be multiples of ${step}¢ and at most the maximum credit` });
      return;
    }

    const body: any = {
      name: name.trim(),
      creditStepCents: step,
      maxCreditCents: maxCents,
    };
    if (validPresets.length > 0) body.presets = validPresets;

    setLoading(true);
    try {
      await api(`/api/locations/${locationId}/vending-machines`, { method: 'POST', body });
      router.replace(`/owner/location/${locationId}` as never);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast.show({ type: 'error', title: 'Equipment name is required' }); return; }
    if (equipType === 'vending') { await handleCreateVending(); return; }

    const body: any = { name: name.trim(), type: equipType };

    if (dualFamily) {
      // The master rate drives metered sessions AND package/custom conversion.
      // Required for self-serve AND vacuums in BOTH pricing families (backend
      // contract — vacuums need it even when selling packages).
      const rateNum = parseFloat(rate);
      if (!rateNum || rateNum <= 0) {
        toast.show({ type: 'error', title: equipType === 'vacuum' ? 'Rate ($/min) is required for vacuums' : 'Rate ($/min) is required' });
        return;
      }
      body.pricePerMinute = rateNum;

      if (activeSection === 'metered') {
        // Metered (count up) family active — no timed packages.
        body.minimumCharge = Math.round(parseFloat(minimumCharge || (equipType === 'vacuum' ? '0' : '3')) * 100);
        const maxCharge = parseFloat(maximumCharge);
        if (!maxCharge || maxCharge <= 0) { toast.show({ type: 'error', title: 'Maximum charge is required for metered pricing' }); return; }
        body.maximumCharge = Math.round(maxCharge * 100);
        if (body.maximumCharge <= body.minimumCharge) {
          toast.show({ type: 'error', title: 'Maximum charge must be greater than minimum charge' });
          return;
        }
        body.allowCustomAmount = false;
      } else {
        // Timed Packages (count down) family active — no metered max charge.
        const pkgs = buildPackages();
        if (!pkgs) return;
        if (pkgs.length === 0) { toast.show({ type: 'error', title: 'Add at least one timed package' }); return; }
        body.tiers = pkgs;
        body.allowCustomAmount = allowCustomAmount;
        // Vacuums keep their Minimum Charge in packages mode too — it's the
        // per-session floor for bundle and membership vacuum sessions.
        if (equipType === 'vacuum') {
          body.minimumCharge = Math.round(parseFloat(minimumCharge || '0') * 100) || 0;
        }
      }
    } else {
      // Automatic: defer tier configuration to the edit screen, which
      // auto-sizes to the attached controller's relay count. Seed disabled
      // placeholder slots so the backend "at least one tier" invariant holds.
      body.tiers = Array.from({ length: DEFAULT_RELAY_COUNT }, (_, i) => ({
        name: `Tier ${i + 1}`,
        price: 0,
        enabled: false,
      }));
    }

    setLoading(true);
    try {
      await api(`/api/locations/${locationId}/bays`, { method: 'POST', body });
      router.replace(`/owner/location/${locationId}` as never);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const noun = equipType === 'vacuum' ? 'vacuum' : 'wash';

  return (
    <>
      <Stack.Screen options={{ title: 'Add Equipment' }} />
      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
          <ContentContainer>
          <Text style={styles.fieldLabel}>Equipment Type</Text>
          <View style={styles.typeGrid}>
            {EQUIP_TYPES.map(t => (
              <TouchableOpacity
                key={t.value}
                style={[styles.typeBtn, equipType === t.value && styles.typeActive]}
                onPress={() => setEquipType(t.value)}
              >
                <Ionicons name={t.icon} size={24} color={equipType === t.value ? colors.primary : colors.textLight} />
                <Text style={[styles.typeText, equipType === t.value && styles.typeTextActive]}>{t.label}</Text>
                <Text style={styles.typeDesc}>{t.desc}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <Input
            label="Name"
            placeholder={equipType === 'vending' ? 'Snack Machine' : equipType === 'vacuum' ? 'Vacuum 1' : 'Bay 1'}
            value={name}
            onChangeText={setName}
            icon={equipType === 'vending' ? 'fast-food-outline' : 'construct-outline'}
            maxLength={32}
          />

          {dualFamily && (
            <View>
              {/* Master rate — drives metered sessions, package minute
                  auto-fill, and custom-amount conversion. */}
              <Input
                label="This unit's rate ($/min)"
                placeholder="0.50"
                value={rate}
                onChangeText={(v: string) => setRate(v.replace(/[^0-9.]/g, ''))}
                keyboardType="decimal-pad"
                icon="cash-outline"
              />
              <Text style={styles.helpText}>
                This bay/vacuum's own per-minute rate — every unit is priced independently. Package prices auto-fill their minutes from it; custom amounts convert at this rate.
              </Text>

              {/* Segmented control — the ENABLE choice: exactly one family is
                  active; whichever button is selected is what the bay runs. */}
              <View style={styles.segmentRow}>
                <TouchableOpacity
                  style={[styles.segmentBtn, activeSection === 'metered' && styles.segmentActive]}
                  onPress={() => setActiveSection('metered')}
                >
                  <Text style={[styles.segmentTitle, activeSection === 'metered' && styles.segmentTitleActive]}>
                    Metered
                  </Text>
                  <Text style={styles.segmentDesc}>Count up — pay for time used with min and max values</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.segmentBtn, activeSection === 'packages' && styles.segmentActive]}
                  onPress={() => {
                    setActiveSection('packages');
                    if (packages.length === 0) setPackages([{ name: '', price: '', minutes: '' }]);
                  }}
                >
                  <Text style={[styles.segmentTitle, activeSection === 'packages' && styles.segmentTitleActive]}>
                    Timed Packages
                  </Text>
                  <Text style={styles.segmentDesc}>Count down — prepaid packages and custom amounts</Text>
                </TouchableOpacity>
              </View>

              {activeSection === 'metered' ? (
                <View style={styles.sectionBody}>
                  <Input
                    label="Minimum Charge ($)"
                    placeholder="3.00"
                    value={minimumCharge}
                    onChangeText={(v: string) => setMinimumCharge(v.replace(/[^0-9.]/g, ''))}
                    keyboardType="decimal-pad"
                    icon="pricetag-outline"
                  />
                  {equipType === 'vacuum' && (
                    <Text style={styles.helpText}>
                      Minimum per session — also applies to bundle and membership vacuum sessions
                    </Text>
                  )}
                  <Input
                    label="Maximum Charge ($)"
                    placeholder="Required"
                    value={maximumCharge}
                    onChangeText={(v: string) => setMaximumCharge(v.replace(/[^0-9.]/g, ''))}
                    keyboardType="decimal-pad"
                    icon="trending-up-outline"
                  />
                  <Text style={styles.helpText}>
                    Customers start a live count-up session and pay for the time they use at the rate above — at least the minimum; the {noun} auto-stops at the maximum.
                  </Text>
                </View>
              ) : (
                <View style={styles.sectionBody}>
                  {/* Vacuum Minimum Charge stays visible in packages mode —
                      bundle/membership sessions run metered under the hood
                      and this is their per-session floor. */}
                  {equipType === 'vacuum' && (
                    <>
                      <Input
                        label="Minimum Charge ($)"
                        placeholder="0.00"
                        value={minimumCharge}
                        onChangeText={(v: string) => setMinimumCharge(v.replace(/[^0-9.]/g, ''))}
                        keyboardType="decimal-pad"
                        icon="pricetag-outline"
                      />
                      <Text style={styles.helpText}>
                        Minimum per session — also applies to bundle and membership vacuum sessions
                      </Text>
                    </>
                  )}
                  <View style={styles.pkgHeaderRow}>
                    <Text style={[styles.pkgHeader, styles.pkgColName]}>Name</Text>
                    <Text style={[styles.pkgHeader, styles.pkgColSmall]}>Price</Text>
                    <Text style={[styles.pkgHeader, styles.pkgColSmall]}>Minutes</Text>
                    {packages.length > 1 && <View style={styles.pkgColRemove} />}
                  </View>
                  {packages.map((p, i) => (
                    <View key={i} style={styles.pkgRow}>
                      <View style={styles.pkgColName}>
                        <Input placeholder="Quick Wash" value={p.name} onChangeText={(v: string) => updatePackage(i, 'name', v)} maxLength={32} />
                      </View>
                      <View style={styles.pkgColSmall}>
                        <Input placeholder="$" value={p.price} onChangeText={(v: string) => updatePackage(i, 'price', v.replace(/[^0-9.]/g, ''))} keyboardType="decimal-pad" />
                      </View>
                      <View style={styles.pkgColSmall}>
                        <Input placeholder="Min" value={p.minutes} onChangeText={(v: string) => updatePackage(i, 'minutes', v.replace(/[^0-9]/g, ''))} keyboardType="numeric" />
                      </View>
                      {packages.length > 1 && (
                        <TouchableOpacity onPress={() => setPackages(packages.filter((_, idx) => idx !== i))} style={styles.pkgColRemove}>
                          <Ionicons name="close-circle" size={24} color={colors.danger} />
                        </TouchableOpacity>
                      )}
                    </View>
                  ))}
                  {packages.length < 6 && (
                    <TouchableOpacity onPress={() => setPackages([...packages, { name: '', price: '', minutes: '' }])} style={styles.addPresetBtn}>
                      <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                      <Text style={styles.addTierText}>Add Package</Text>
                    </TouchableOpacity>
                  )}
                  <Text style={styles.helpText}>
                    {overrideMinutes
                      ? 'Minutes are unlocked — set any minutes for a price (bulk discounts).'
                      : 'Price and minutes stay in sync at the rate above — type either one and the other fills in.'}
                  </Text>
                  <TouchableOpacity style={styles.checkRow} onPress={() => setOverrideMinutes(!overrideMinutes)}>
                    <Ionicons name={overrideMinutes ? 'checkbox' : 'square-outline'} size={24} color={overrideMinutes ? colors.primary : colors.textLight} />
                    <Text style={styles.checkLabel}>Override minutes (bulk discounts)</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.checkRow} onPress={() => setAllowCustomAmount(!allowCustomAmount)}>
                    <Ionicons name={allowCustomAmount ? 'checkbox' : 'square-outline'} size={24} color={allowCustomAmount ? colors.primary : colors.textLight} />
                    <Text style={styles.checkLabel}>Allow custom amounts</Text>
                  </TouchableOpacity>
                  <Text style={styles.helpText}>Custom amounts convert to time at the master rate.</Text>
                </View>
              )}
            </View>
          )}

          {equipType === 'automatic' && (
            <View style={styles.tierNotice}>
              <Ionicons name="information-circle" size={20} color={colors.primary} />
              <Text style={styles.tierNoticeText}>
                Automatics can be configured with any Washes or Upsells configured on the Wash and Upsell page.
              </Text>
            </View>
          )}

          {equipType === 'vending' && (
            <View>
              <Input label="Credit per pulse (cents)" value={creditStepCents} onChangeText={(v: string) => setCreditStepCents(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" icon="cash-outline" />
              <Text style={styles.helpText}>What one relay pulse is worth on the machine's coin acceptor (25 for most). Pulse timing uses the board defaults.</Text>
              <Input label="Maximum Credit ($)" value={maxCredit} onChangeText={(v: string) => setMaxCredit(v.replace(/[^0-9.]/g, ''))} keyboardType="decimal-pad" icon="trending-up-outline" />
              <Text style={styles.fieldLabel}>Credit amounts</Text>
              <Text style={styles.helpText}>
                Up to 6 amounts customers can buy; each delivers the same amount in credit. If empty, customers see $1 / $2 / $3.
              </Text>
              <View style={styles.presetsWrap}>
                {vendPresets.map((p, i) => (
                  <View key={i} style={styles.presetRow}>
                    <View style={{ flex: 1 }}>
                      <Input placeholder="$ amount" value={p} onChangeText={(v: string) => { const u = [...vendPresets]; u[i] = v.replace(/[^0-9.]/g, ''); setVendPresets(u); }} keyboardType="decimal-pad" />
                    </View>
                    <TouchableOpacity onPress={() => setVendPresets(vendPresets.filter((_, idx) => idx !== i))} style={styles.removeTier}>
                      <Ionicons name="close-circle" size={24} color={colors.danger} />
                    </TouchableOpacity>
                  </View>
                ))}
                {vendPresets.length < 6 && (
                  <TouchableOpacity onPress={() => setVendPresets([...vendPresets, ''])} style={styles.addPresetBtn}>
                    <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                    <Text style={styles.addTierText}>Add Amount</Text>
                  </TouchableOpacity>
                )}
              </View>
              <View style={styles.tierNotice}>
                <Ionicons name="information-circle" size={20} color={colors.primary} />
                <Text style={styles.tierNoticeText}>
                  Wire the controller relay in parallel with the machine's coin acceptor. The app sells credit amounts — customers pick the item at the machine.
                </Text>
              </View>
            </View>
          )}

          <Button
            title={equipType === 'vending' ? 'Add Machine' : equipType === 'vacuum' ? 'Create Vacuum' : 'Create Bay'}
            onPress={handleCreate}
            loading={loading}
            size="lg"
            style={styles.createBtn}
          />
          <Button title="Cancel" onPress={() => router.back()} variant="outline" size="md" style={styles.cancelBtn} />
          </ContentContainer>
        </ScrollView>
      </KeyboardAvoidingView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { padding: spacing.lg },
  fieldLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, marginBottom: spacing.sm },
  typeGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.lg },
  typeBtn: {
    flexGrow: 1, flexBasis: '45%', padding: spacing.md, borderRadius: borderRadius.md,
    borderWidth: 2, borderColor: c.border,
    alignItems: 'center', gap: spacing.xs,
  },
  typeActive: { borderColor: c.primary, backgroundColor: c.primaryBg },
  typeText: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.textSecondary, textAlign: 'center' },
  typeTextActive: { color: c.primary },
  typeDesc: { fontSize: fontSize.xs, color: c.textLight, textAlign: 'center' },
  // Segmented pricing-family control
  segmentRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md },
  segmentBtn: {
    flex: 1, padding: spacing.md, borderRadius: borderRadius.md,
    borderWidth: 2, borderColor: c.border, gap: spacing.xs,
  },
  segmentActive: { borderColor: c.primary, backgroundColor: c.primaryBg },
  segmentTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.textSecondary },
  segmentTitleActive: { color: c.primary },
  segmentDesc: { fontSize: fontSize.xs, color: c.textLight },
  sectionBody: { marginBottom: spacing.sm },
  // Package row editor
  pkgHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs },
  pkgHeader: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, textTransform: 'uppercase' as const },
  pkgRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  pkgColName: { flex: 2 },
  pkgColSmall: { flex: 1 },
  pkgColRemove: { width: 28, paddingBottom: spacing.md, alignItems: 'center' as const },
  addTierText: { fontSize: fontSize.sm, color: c.primaryDark, fontWeight: fontWeight.semibold },
  removeTier: { paddingBottom: spacing.md },
  helpText: { fontSize: fontSize.xs, color: c.textLight, marginTop: -spacing.sm, marginBottom: spacing.md },
  createBtn: { marginTop: spacing.md },
  cancelBtn: { marginTop: spacing.sm, marginBottom: spacing.xxl },
  presetsWrap: { marginBottom: spacing.sm },
  presetRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  addPresetBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.sm },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, marginBottom: spacing.xs },
  checkLabel: { fontSize: fontSize.sm, color: c.text },
  advancedCard: { marginBottom: spacing.md },
  relayRow: { flexDirection: 'row', gap: spacing.sm },
  autoPulseRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginBottom: spacing.xs },
  autoPulseLabel: { flex: 1, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.primary },
  autoPulseInput: { flex: 1 },
  tierNotice: {
    flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm,
    padding: spacing.md, marginBottom: spacing.md,
    borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.primary + '40',
    backgroundColor: c.primary + '10',
  },
  tierNoticeText: { flex: 1, fontSize: fontSize.sm, color: c.text, lineHeight: 20 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
